import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./AccessoriesHero.css";
import heroImg from "../../../../assets/images/accessories/acchero.png";
import { resolveMediaUrl } from "../../../../utils/media";
import { usePageContent } from "../../../../hooks/usePublicQueries";

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const findSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const unwrapSectionData = (section) => {
  if (section == null) return section;
  if (typeof section === "string") {
    const t = section.trim();
    if (t.startsWith("{")) {
      try {
        const parsed = JSON.parse(t);
        if (parsed && typeof parsed === "object") return unwrapSectionData(parsed);
      } catch {
        return section;
      }
    }
    return section;
  }
  if (typeof section !== "object") return section;
  if (section.data && typeof section.data === "object") return section.data;
  return section;
};

/** Mirrors the field aliases used by OtherProducts so the same CMS image is shown here. */
const getItemImageFromOtherProducts = (content, itemNumber) => {
  if (!content || typeof content !== "object") return "";
  const suffix = String(itemNumber);
  const candidates = [
    content[`item${suffix}Image`],
    content[`item${suffix}image`],
    content[`item${suffix}Img`],
    content[`item${suffix}img`],
    content[`item${suffix}Icon`],
    content[`item${suffix}icon`],
    content[`item${suffix}Media`],
    content[`item${suffix}media`],
    content[`item${suffix}MediaUrl`],
    content[`item${suffix}mediaUrl`],
    content[`item${suffix}Photo`],
    content[`item${suffix}photo`],
  ];
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const VARIANT_KEYS = {
  accessories: "accessories",
  drywall: "drywall",
  ceiling: "ceiling",
};

const AccessoriesHero = ({ variant = "accessories", imageSlot, image }) => {
  const { t, i18n } = useTranslation();
  const shouldFetch = !image && Boolean(imageSlot);
  const { data: homeContent } = usePageContent("home", i18n.language, { enabled: shouldFetch });
  const heroKey = VARIANT_KEYS[variant] || "accessories";
  const title = t(`sections.products.categoryHero.${heroKey}.title`);
  const description = t(`sections.products.categoryHero.${heroKey}.desc`);

  const apiImage = useMemo(() => {
    if (!shouldFetch) return "";
    const section = unwrapSectionData(findSection(homeContent, "otherproducts"));
    return getItemImageFromOtherProducts(section, imageSlot) || "";
  }, [homeContent, imageSlot, shouldFetch]);

  const rightImage = image || apiImage || heroImg;

  return (
    <section className="accessoriesHero">
      <div className="accessoriesHero-container">
        <div className="accessoriesHero-left">
          <span className="accessoriesHero-tag">{t("sections.products.categoryHero.tag")}</span>

          <h1 className="accessoriesHero-title">{title}</h1>

          <p className="accessoriesHero-desc">{description}</p>

          <div className="accessoriesHero-link">
            {t("sections.products.categoryHero.exploreAll")}
            <span className="arrow" aria-hidden="true">
              ↓
            </span>
          </div>
        </div>

        <div className="accessoriesHero-right">
          <img
            src={rightImage}
            alt={title}
            draggable={false}
            onError={(e) => {
              if (e.currentTarget.src !== heroImg) {
                e.currentTarget.src = heroImg;
              }
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default AccessoriesHero;
